import { useState, useEffect } from "react";
import axios from "axios";
import { X, ArrowLeft, ArrowRight, FileText, Loader2, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CORRECTIVE_MEASURES = [
  { v: "A", l: "A) Reparație / înlocuire piese" },
  { v: "B", l: "B) Înlocuirea produsului cu unul nou" },
  { v: "C", l: "C) Reducerea prețului" },
  { v: "D", l: "D) Încetare contract și returnare bani" },
];

const WARRANTY_EXTENSIONS = [
  { v: "REP_DAYS", l: "Reparație — prelungită cu zilele de staționare" },
  { v: "REPL_24", l: "Înlocuire — 24 luni noi de garanție" },
  { v: "REPL_12", l: "Înlocuire — 12 luni noi de garanție" },
];

export default function PvServiceModal({ returnId, onClose, onGenerated, mode = "pv_only", onAwbCreated }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    client_type: "PF",
    customer_name: "",
    order_number: "",
    order_date: "",
    customer_phone: "",
    customer_email: "",
    reclaimed_product: "",
    request_date: "",
    receipt_date: "",
    reclaimed_issues: "",
    findings: "",
    under_warranty: "DA",
    corrective_measure: "",
    warranty_extension: "",
    extension_days: "",
    new_warranty_until: "",
    resolution_date: "",
    other_notes: "",
  });

  useEffect(() => {
    axios.get(`${API}/returns/${returnId}/pv-service/data`)
      .then((r) => setForm((f) => ({ ...f, ...r.data })))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [returnId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-compute "Noua garanție este valabilă până la data de" based on:
  //   order_date + 24 months (PF) or 12 months (PJ) + extension_days.
  // User request 2026-07-23: shown on PV page 3, editable but auto-populated.
  // Auto-compute `new_warranty_until` in two scenarios:
  //   1. Feb 2026: la REPL_24/REPL_12, noua garantie = data PV + 24/12 luni
  //      (independent de order_date — se pleaca de la data procesului verbal).
  //   2. Legacy: la REP_DAYS/PROLONG, noua garantie = order_date + 24 luni
  //      (PF) sau 12 luni (PJ) + extension_days.
  useEffect(() => {
    const we = (form.warranty_extension || "").toUpperCase();
    // Case 1 — inlocuire produs nou → garantie de la data PV
    if (we === "REPL_24" || we === "REPL_12") {
      const pvDateStr = form.resolution_date || new Date().toISOString().slice(0, 10);
      const base = new Date(pvDateStr);
      if (isNaN(base.getTime())) return;
      const months = we === "REPL_24" ? 24 : 12;
      const d = new Date(base);
      d.setMonth(d.getMonth() + months);
      const iso = d.toISOString().slice(0, 10);
      setForm((f) => (f.new_warranty_until === iso ? f : { ...f, new_warranty_until: iso }));
      return;
    }
    // Case 2 — prelungire zile → garantie de la order_date
    if (!form.order_date) return;
    const base = new Date(form.order_date);
    if (isNaN(base.getTime())) return;
    const months = (form.client_type || "PF").toUpperCase() === "PJ" ? 12 : 24;
    const d = new Date(base);
    d.setMonth(d.getMonth() + months);
    const extra = parseInt(form.extension_days || "0", 10);
    if (!isNaN(extra) && extra > 0) d.setDate(d.getDate() + extra);
    const iso = d.toISOString().slice(0, 10);
    setForm((f) => (f.new_warranty_until === iso ? f : { ...f, new_warranty_until: iso }));
  }, [form.order_date, form.client_type, form.extension_days, form.warranty_extension, form.resolution_date]);

  const submit = async () => {
    setBusy(true); setError("");
    try {
      // Combined flow: PV PDF + AWB Livrare + status email (PV attached).
      // The backend returns JSON in this mode — parent shows an AWB popup.
      if (mode === "pv_and_deliver") {
        const r = await axios.post(`${API}/returns/${returnId}/pv-and-deliver`, form);
        onAwbCreated?.(r.data);
        onGenerated?.();
        onClose();
        return;
      }
      // Legacy flow: just generate the PV PDF and auto-print it.
      const r = await axios.post(`${API}/returns/${returnId}/pv-service`, form, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      // 1) Open the PDF in a new tab so the user can preview it.
      window.open(url, "_blank");
      // 2) Trigger the browser's print dialog via a hidden iframe pointing at
      //    the same blob URL. This is the most reliable cross-browser way to
      //    auto-print a PDF generated client-side. The iframe is removed after
      //    the print dialog is closed (or after 60s as a safety net).
      const frame = document.createElement("iframe");
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      frame.src = url;
      const cleanup = () => {
        try { frame.parentNode && frame.parentNode.removeChild(frame); } catch { /* ignore */ }
        try { window.URL.revokeObjectURL(url); } catch { /* ignore */ }
      };
      frame.onload = () => {
        // small delay so the PDF viewer finishes loading inside the frame
        setTimeout(() => {
          try {
            frame.contentWindow && frame.contentWindow.focus();
            frame.contentWindow && frame.contentWindow.print();
          } catch { /* ignore */ }
          // cleanup later so the print job has time to spool
          setTimeout(cleanup, 60000);
        }, 600);
      };
      document.body.appendChild(frame);
      onGenerated?.();
      onClose();
    } catch (e) {
      try {
        const text = await e.response?.data?.text?.();
        const j = text ? JSON.parse(text) : null;
        setError(j?.detail || e.message);
      } catch {
        setError(e.response?.data?.detail || e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const next = () => setStep((s) => Math.min(3, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="pv-modal">
      <div className="w-full max-w-3xl rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-orange-400" />
            <div>
              <h2 className="text-sm font-semibold text-zinc-100">
                {mode === "pv_and_deliver" ? "PV Service + AWB Livrare" : "Generare PV Service"}
              </h2>
              <p className="text-[10px] text-zinc-500">
                Pas {step} din 3
                {mode === "pv_and_deliver" && " · La final: AWB Livrare generat + email cu PV atașat"}
              </p>
            </div>
          </div>
          <button data-testid="pv-close" onClick={onClose} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex gap-1 px-5 pt-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-1 flex-1 rounded ${n <= step ? "bg-orange-500" : "bg-zinc-800"}`} />
          ))}
        </div>

        {/* Body */}
        <div className="max-h-[65vh] overflow-y-auto px-5 py-4">
          {step === 1 && <Step1 form={form} set={set} />}
          {step === 2 && <Step2 form={form} set={set} />}
          {step === 3 && <Step3 form={form} set={set} />}
          {error && <div className="mt-3 rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error}</div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-zinc-800 bg-zinc-900/40 px-5 py-3">
          <button
            data-testid="pv-prev"
            disabled={step === 1}
            onClick={prev}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:text-zinc-100 disabled:opacity-40"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Înapoi
          </button>
          {step < 3 ? (
            <button data-testid="pv-next" onClick={next} className="inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-4 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-orange-400">
              Continuă <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button data-testid="pv-generate" onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              {mode === "pv_and_deliver" ? "Generează PV + AWB Livrare" : "Generează PV PDF"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testId, full = false }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-400">{label}</span>
      {children}
    </label>
  );
}
function Inp({ value, onChange, testId, type = "text", placeholder = "" }) {
  return <input data-testid={testId} type={type} value={value || ""} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full rounded border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 focus:border-orange-500/50 focus:outline-none" />;
}
function TextArea({ value, onChange, testId, rows = 3, placeholder = "" }) {
  return <textarea data-testid={testId} rows={rows} value={value || ""} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full rounded border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 focus:border-orange-500/50 focus:outline-none" />;
}
function Select({ value, onChange, options, testId, placeholder }) {
  return (
    <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 focus:border-orange-500/50 focus:outline-none">
      <option value="">{placeholder || "— alege —"}</option>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}
function RadioRow({ value, onChange, options, testId }) {
  return (
    <div className="flex gap-2" data-testid={testId}>
      {options.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)}
          className={`flex-1 rounded border px-2 py-1.5 text-xs transition ${value === o.v ? "border-orange-500/60 bg-orange-500/15 text-orange-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function Step1({ form, set }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Field label="Tip client">
        <RadioRow value={form.client_type} onChange={(v) => set("client_type", v)} testId="pv-client-type"
          options={[{ v: "PF", l: "Persoană fizică" }, { v: "PJ", l: "Persoană juridică" }]} />
      </Field>
      <Field label="Nume client"><Inp testId="pv-customer-name" value={form.customer_name} onChange={(v) => set("customer_name", v)} /></Field>
      <Field label="Dovada achiziție (nr. comandă)"><Inp testId="pv-order-number" value={form.order_number} onChange={(v) => set("order_number", v)} /></Field>
      <Field label="Telefon"><Inp testId="pv-customer-phone" value={form.customer_phone} onChange={(v) => set("customer_phone", v)} /></Field>
      <Field label="Email"><Inp testId="pv-customer-email" value={form.customer_email} onChange={(v) => set("customer_email", v)} /></Field>
      <Field label="Produs reclamat" full><Inp testId="pv-reclaimed-product" value={form.reclaimed_product} onChange={(v) => set("reclaimed_product", v)} /></Field>
      <Field label="Data cererii de garanție/service"><Inp testId="pv-request-date" type="date" value={form.request_date} onChange={(v) => set("request_date", v)} /></Field>
      <Field label="Data recepție produs în service"><Inp testId="pv-receipt-date" type="date" value={form.receipt_date} onChange={(v) => set("receipt_date", v)} /></Field>
      <Field label="Deficiențe / neconformități reclamate de client" full>
        <TextArea testId="pv-reclaimed-issues" rows={4} value={form.reclaimed_issues} onChange={(v) => set("reclaimed_issues", v)}
          placeholder="Descrie problemele semnalate de client..." />
      </Field>
    </div>
  );
}

function Step2({ form, set }) {
  return (
    <div className="space-y-3">
      <Field label="Constatări efectuate de unitatea service">
        <TextArea testId="pv-findings" rows={6} value={form.findings} onChange={(v) => set("findings", v)}
          placeholder="Descrie ce s-a constatat la verificare/diagnoză..." />
      </Field>
      <Field label="Produsul face obiectul garanției?">
        <RadioRow value={form.under_warranty} onChange={(v) => set("under_warranty", v)} testId="pv-under-warranty"
          options={[{ v: "DA", l: "DA" }, { v: "NU", l: "NU" }]} />
      </Field>
    </div>
  );
}

function Step3({ form, set }) {
  return (
    <div className="grid grid-cols-1 gap-3">
      <Field label="Măsură corectivă sub garanție">
        <Select testId="pv-corrective" value={form.corrective_measure} onChange={(v) => set("corrective_measure", v)} options={CORRECTIVE_MEASURES} placeholder="— alege măsura —" />
      </Field>
      <Field label="Prelungire garanție">
        <Select testId="pv-warranty-ext" value={form.warranty_extension} onChange={(v) => set("warranty_extension", v)} options={WARRANTY_EXTENSIONS} placeholder="— alege prelungirea —" />
      </Field>
      {form.warranty_extension === "REP_DAYS" && (
        <Field label="Zile de staționare în service (prelungirea garanției)">
          <div className="flex items-center gap-2">
            <Inp testId="pv-extension-days" type="number" value={form.extension_days} onChange={(v) => set("extension_days", v)} placeholder="ex: 5" />
            <span className="text-[10px] text-zinc-400">zile</span>
          </div>
        </Field>
      )}
      <Field label="Noua garanție este valabilă până la data de (auto)">
        <div className="space-y-1">
          <Inp testId="pv-new-warranty-until" type="date" value={form.new_warranty_until} onChange={(v) => set("new_warranty_until", v)} />
          <p className="text-[10px] text-zinc-500">
            Calculată automat din data comenzii ({form.order_date || "—"}) + {(form.client_type || "PF").toUpperCase() === "PJ" ? "12" : "24"} luni ({form.client_type === "PJ" ? "PJ" : "PF"})
            {form.extension_days && parseInt(form.extension_days, 10) > 0 ? ` + ${form.extension_days} zile staționare` : ""}
            . Poți edita manual dacă e nevoie.
          </p>
        </div>
      </Field>
      <Field label="Data comunicării rezoluției (max 15 zile de la primire)">
        <Inp testId="pv-resolution-date" type="date" value={form.resolution_date} onChange={(v) => set("resolution_date", v)} />
      </Field>
      <Field label="Alte mențiuni">
        <TextArea testId="pv-other-notes" rows={4} value={form.other_notes} onChange={(v) => set("other_notes", v)}
          placeholder="Observații suplimentare, instrucțiuni client etc." />
      </Field>
    </div>
  );
}
